import { DEFAULT_VIEWER_LOCALE, parseViewerLocale, type ViewerLocale } from '@/viewer-locale/domain/ViewerLocale';

/**
 * Host-pushed viewer locale, readable from React and non-React code alike.
 *
 * A module-level store rather than a React context on purpose: CRM's
 * `QuoteTemplatePickerDialog` mounts the federated `TemplateRow` without a
 * `HostConfigProvider`, and `formatTemplateListDate` is a plain helper with no hook access.
 * A store serves both; a context would silently leave those on defaults.
 */
let current: ViewerLocale | null = null;
/**
 * Cached org-default fallback — must be referentially stable for `useSyncExternalStore`.
 * The organization (via the US preset default), not the browser machine, defines the zone.
 */
let cachedFallback: ViewerLocale | null = null;
const listeners = new Set<() => void>();

function getFallbackLocale(): ViewerLocale {
  if (!cachedFallback) {
    cachedFallback = DEFAULT_VIEWER_LOCALE;
  }
  return cachedFallback;
}

function sameLocale(a: ViewerLocale, b: ViewerLocale): boolean {
  return (
    a.dateFormat === b.dateFormat &&
    a.numberFormat === b.numberFormat &&
    a.timeZone === b.timeZone &&
    a.cultureTag === b.cultureTag &&
    a.timeFormat === b.timeFormat &&
    a.measurementUnits === b.measurementUnits &&
    a.currency === b.currency
  );
}

/**
 * Publishes organization formats plus the viewer's display timezone (LLAMS-469).
 * Called by the host across the federation boundary, so the payload is untrusted —
 * a malformed bag is ignored rather than applied.
 */
export function setViewerLocale(raw: unknown): void {
  const parsed = parseViewerLocale(raw);
  if (!parsed) return;
  if (current && sameLocale(current, parsed)) return;

  current = parsed;
  listeners.forEach((listener) => listener());
}

/**
 * Reads the active viewer locale. Falls back to the US organization preset
 * (`America/New_York`), which is what standalone mode (no host) and the pre-push
 * first render both need — never the browser's IANA zone.
 *
 * The fallback object is cached so consecutive reads return the same reference — required by
 * `useSyncExternalStore` (Object.is snapshot compare), otherwise React enters an update loop.
 */
export function getViewerLocale(): ViewerLocale {
  return current ?? getFallbackLocale();
}

/** Subscribes to changes; returns the unsubscribe function (`useSyncExternalStore` contract). */
export function subscribeToViewerLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Clears the pushed locale. Intended for test teardown. */
export function resetViewerLocale(): void {
  current = null;
  listeners.forEach((listener) => listener());
}
